import { formatDateHumanTimeOnly, formatTimeFromMinutes } from '@mixins/date-formatter-mixin';
import { createNewDate, createNewDateForBranch, createNewDuration } from '@mixins/moment-mixin.js';
import Model from '@models/model';
// eslint-disable-next-line import/no-cycle
import RouteTask, { ROUTE_TASK_STATUS_ARRIVED, ROUTE_TASK_STATUS_COMPLETED } from '@models/route-task';
import ceil from 'lodash/ceil';
import round from 'lodash/round';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';
import RandomColor from 'randomcolor';

import ApiClient from '@/api-client';

export const ROUTE_STATUS_COMPLETED = 'Completed';
export const ROUTE_STATUS_IN_PROGRESS = 'In Progress';
export const ROUTE_STATUS_NOT_STARTED = 'Not Started';
export const ROUTE_STATUS_UNASSIGNED = 'Unassigned';
export const ROUTE_STATUS_VOIDED = 'Voided';
export const ROUTE_TYPE_DELIVERY = 'Delivery';
export const ROUTE_TYPE_SERVICE = 'Service';

export const ROUTE_STATUSES = [
    { id: ROUTE_STATUS_COMPLETED, text: ROUTE_STATUS_COMPLETED },
    { id: ROUTE_STATUS_IN_PROGRESS, text: ROUTE_STATUS_IN_PROGRESS },
    { id: ROUTE_STATUS_NOT_STARTED, text: ROUTE_STATUS_NOT_STARTED },
    { id: ROUTE_STATUS_UNASSIGNED, text: ROUTE_STATUS_UNASSIGNED },
];
export default class Route extends Model {
    static className = 'Route';

    static allowsEdit = true;

    static formFieldsConfig = {
        status: {
            type: 'select',
            options: ROUTE_STATUSES,
        },
        finish_time: {
            help: 'The time the route was completed format is 2025-05-20 14:00:00.000000 and timezone is UTC',
        },
    }

    static allowedFieldEdits = [
        'status',
        'finish_time',
    ];

    relations() {
        return {
            tasks: RouteTask,
        };
    }

    get propCasts() {
        return {
            estimated_start_time: [createNewDateForBranch, this.branch],
            estimated_finish_time: [createNewDateForBranch, this.branch],
            finish_time: [createNewDateForBranch, this.branch],
            start_time: [createNewDateForBranch, this.branch],
        };
    }

    get formFieldCasts() {
        return {
            estimated_start_time: [formatDateHumanTimeOnly, this.branch.timezone],
            start_time: [formatDateHumanTimeOnly, this.branch.timezone],
        };
    }

    resource() {
        return 'routes';
    }

    calendar_type = 'route';

    pendingOptimizing = false;

    optimizing= false;

    branch = {};

    vehicle = {};

    get color() {
        return this.vehicle?.vehicle_color?.color || RandomColor({
            luminosity: 'dark',
            hue: 'random',
            seed: this.user?.phone || this.id,
        });
    }

    points() {
        return this.routeTasksSortedByStatus.filter((t) => !t.isTimeEvent).map((t) => ({
            lat: t.branch_id ? t.branch.lat : t.order.customer_location.delivery_latitude,
            lng: t.branch_id ? t.branch.lng : t.order.customer_location.delivery_longitude,
            taskId: t.id,
            orderId: t.order_id,
            branch: !!t.branch_id,
            title: t.branch_id ? t.branch.name : t.order.customer_location.customer.name,
            status: t.status,
            address: t.branch_id ? t.branch.address : t.order.customer_location.delivery_address_line_1,
        }));
    }

    totalPlannedTime() {
        return `${formatTimeFromMinutes(this.plannedDeliveryMinutes())}`;
    }

    totalActualTime() {
        return `${formatTimeFromMinutes(this.actualDeliveryMinutes())}`;
    }

    hasException() {
        return this.status === ROUTE_STATUS_COMPLETED && this.tasks.filter((task) => task.order_id).filter((task) => task.order.has_exception).length;
    }

    vehicleCapacity() {
        if (this.vehicle) {
            const weightCapacity = round((this.totalWeight() * 100) / this.vehicle.weight_capacity, 0);
            const sizeCapacity = round((this.totalSize() * 100) / this.vehicle.size_capacity, 0);
            return weightCapacity > sizeCapacity ? weightCapacity : sizeCapacity;
        }
        return 0;
    }

    totalWeight() {
        return sumBy(this.tasks.filter((task) => task.order_id), (task) => {
            const tankTypeItems = task.getTanks();
            return sumBy(tankTypeItems, (item) => item.getItem().weight * item.qty);
        });
    }

    totalSize() {
        return sumBy(this.tasks.filter((t) => t.order_id), (t) => {
            const tankTypeItems = t.getTanks();

            return sumBy(tankTypeItems, (item) => {
                let { qty } = item;
                if (item.getItem().puretec_item_number === '08-001') {
                    qty = item.qty >= 7 ? ceil(item.qty / 49) : 0.15;
                }
                return item.getItem().size * qty;
            });
        });
    }

    endTime() {
        let estimatedEndTime;

        if (this.status === ROUTE_STATUS_COMPLETED) {
            estimatedEndTime = this.finish_time;
        } else if (this.status === ROUTE_STATUS_IN_PROGRESS) {
            const tasksOnly = this.routeTasksSortedByStatus.filter((t) => !t.isTimeEvent);
            const lastTask = tasksOnly[tasksOnly.length - 1];
            estimatedEndTime = lastTask?.estimated_start_time;
        } else {
            estimatedEndTime = this.estimated_finish_time;
        }

        return estimatedEndTime;
    }

    getStartTime() {
        return this.start_time || this.estimated_start_time || createNewDate();
    }

    actualTime() {
        const end = this.endTime();
        if (!end || !this.start_time) {
            return 'N/A';
        }

        const timeDifference = createNewDuration(end.diff(this.start_time));

        return formatTimeFromMinutes(timeDifference.asMinutes());
    }

    routeStartsOnDifferentDay() {
        const startTime = this.getStartTime();
        if (startTime) {
            return this.getStartTime().format('YYYY-MM-DD') !== this.date;
        }
        return false;
    }

    routeFinishesOnDifferentDay() {
        const startTime = this.getStartTime();
        const endTime = this.endTime();
        if (startTime && endTime) {
            return startTime.format('YYYY-MM-DD') !== endTime.format('YYYY-MM-DD');
        }
        return false;
    }

    get routeTasksSortedByStatus() {
        const completedTasks = this.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_COMPLETED);
        const arrivedTasks = this.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_ARRIVED);
        const remainingTasks = this.tasks.filter((task) => task.status === null);

        const sorted = [...sortBy(completedTasks, 'completed_at'), ...sortBy(arrivedTasks, 'arrived_at'), ...sortBy(remainingTasks, 'estimated_start_time')];

        if (!this.time_events || !this.time_events.length) {
            return sorted;
        }

        const timeEvents = this.time_events.map((te) => ({
            isTimeEvent: true,
            type: te.type,
            logged_at: te.logged_at,
            cat_1: te.cat_1,
            sortTime: createNewDate(te.logged_at),
        }));

        let teIndex = 0;
        const result = sorted.reduce((acc, task) => {
            const taskTime = task.completed_at || task.arrived_at || task.estimated_start_time;
            const taskMoment = taskTime ? createNewDate(taskTime) : null;

            while (teIndex < timeEvents.length && taskMoment) {
                const te = timeEvents[teIndex];
                const isAfterEvent = te.type === 'prevailing_wage_end';
                const shouldInsert = isAfterEvent
                    ? te.sortTime.isBefore(taskMoment)
                    : te.sortTime.isSameOrBefore(taskMoment);
                if (!shouldInsert) break;
                acc.push(te);
                teIndex++;
            }
            acc.push(task);
            return acc;
        }, []);

        while (teIndex < timeEvents.length) {
            result.push(timeEvents[teIndex]);
            teIndex++;
        }

        return result;
    }

    completedTasks() {
        return this.tasks.filter((t) => t.status === ROUTE_TASK_STATUS_COMPLETED && t.order_id).length;
    }

    completedPercentage() {
        const completedTasks = this.tasks.filter((t) => t.status === ROUTE_TASK_STATUS_COMPLETED && t.order_id);

        return Number((completedTasks.length / this.tasks.filter(((t) => t.order_id)).length) * 100).toFixed(0);
    }

    plannedDeliveryMinutes() {
        return createNewDuration(createNewDate(this.estimated_finish_time).diff(this.estimated_start_time)).asMinutes() - this.total_meal_breaks_minutes;
    }

    vehicleShiftInMinutes() {
        return this.vehicle ? this.vehicle.shift_hours * 60 : 480;
    }

    actualDeliveryMinutes() {
        return createNewDate(this.endTime()).diff(this.start_time, 'minutes');
    }

    driverIsDelayed() {
        const totalDelayMinutes = this.driverIsDelayedBy();

        let symbol = '+';
        if (Math.sign(totalDelayMinutes) === -1) {
            symbol = '-';
        }

        return symbol === '-';
    }

    driverIsDelayedBy() {
        return this.plannedDeliveryMinutes() - this.actualDeliveryMinutes();
    }

    allTasksCompleted() {
        return this.tasks.filter((task) => task.status === ROUTE_TASK_STATUS_COMPLETED).length === this.tasks.length;
    }

    routeHasSharedOrder() {
        return !!this.tasks.filter((t) => t.parent_route_task_id || t.has_children).length;
    }

    isDelivery() {
        return this.type === ROUTE_TYPE_DELIVERY;
    }

    async optimizeRoute(byPass = false) {
        if (byPass) {
            this.optimizing = true;
        } else {
            this.pendingOptimizing = true;
        }
        try {
            if (this.type === ROUTE_TYPE_DELIVERY) {
                await ApiClient.post(`/api/routes/${this.id}/optimize?bypass=${byPass ? 1 : 0}`);
            } else {
                await ApiClient.post(`/api/routes/${this.id}/optimize?bypass=${byPass ? 1 : 0}`);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    actualDeliveryTimeMinutes() {
        const ranges = this.tasks
            .filter((t) => t.status === ROUTE_TASK_STATUS_COMPLETED && t.order_id && t.arrived_at && t.completed_at)
            .map((t) => ({ start: createNewDate(t.arrived_at).valueOf(), end: createNewDate(t.completed_at).valueOf() }))
            .sort((a, b) => a.start - b.start);

        const merged = ranges.reduce((acc, range) => {
            const last = acc[acc.length - 1];
            if (last && range.start <= last.end) {
                last.end = Math.max(last.end, range.end);
            } else {
                acc.push({ start: range.start, end: range.end });
            }
            return acc;
        }, []);

        return sumBy(merged, (r) => (r.end - r.start) / 60000);
    }

    actualDrivingMinutes() {
        return this.actualDeliveryMinutes() - this.actualDeliveryTimeMinutes();
    }

    hasCoDriver() {
        return !!this.co_driver_user_id;
    }

    async confirm(confirm = true) {
        await ApiClient.patch(`/api/routes/${this.id}/confirm`, { confirm });
        this.confirmed = confirm;
    }

    isStatic() {
        return !this.vehicle?.is_dynamic;
    }

    hasVehicle() {
        return !!this.vehicle_id;
    }

    isCompleted() {
        return this.status === ROUTE_STATUS_COMPLETED;
    }

    async getLoadSheet() {
        return this.api().get(`/api/routes/${this.id}/load-sheet`);
    }
}
